import styled, { css } from 'styled-components/macro'
import {
    getResponsiveProp,
    getResponsiveStyles,
    resolveResponsiveProp,
} from 'styles/mixins'
import { Spacing, spacingMixin } from 'styles/spacing'
import { useResolutions } from 'store/resolutions'
import { Color, Elevation, ResponsiveSize } from 'styles/types'

export type Direction = 'row' | 'column' | 'column-reverse' | 'row-reverse'

type Justify =
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'space-evenly'
    | 'stretch'

export type Align =
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'stretch'
    | 'baseline'

type Wrap = 'wrap' | 'nowrap'

export const overflowOptions = [
    'auto',
    'inherit',
    'initial',
    'hidden',
    'overlay',
    'revert',
    'scroll',
    'unset',
    'visible',
] as const
export type Overflow = typeof overflowOptions[number]
export type ResponsiveOverflow = Overflow | [Overflow, Overflow]
export interface BoxProps extends Spacing {
    direction?: Direction | [Direction, Direction]
    justify?: Justify | [Justify, Justify]
    align?: Align | [Align, Align]
    alignSelf?: Align | [Align, Align]
    wrap?: Wrap | [Wrap, Wrap]
    gap?: number | [number, number]
    minWidth?: number | [number, number]
    maxWidth?: number | [number, number]
    minHeight?: number | [number, number]
    isFullWidth?: boolean
    hasNoShrink?: boolean
    bgColor?: Color
    width?: ResponsiveSize
    height?: ResponsiveSize
    borderColor?: Color
    borderWidth?: number
    borderRadius?: ResponsiveSize
    shadow?: Elevation
    overflow?: ResponsiveOverflow
}

const Box = styled.div<BoxProps>`
    display: flex;
    ${({ width }) => width && getResponsiveStyles(width, 'width')}
    ${({ height }) => height && getResponsiveStyles(height, 'height')}

    ${({ isFullWidth }) =>
        isFullWidth &&
        css`
            width: 100%;
        `}

    ${({ hasNoShrink }) =>
        hasNoShrink &&
        css`
            flex-shrink: 0;
        `}

    ${({ shadow, theme }) =>
        shadow &&
        css`
            box-shadow: ${theme.elevation[shadow]};
        `}

    ${({ borderRadius }) =>
        borderRadius && getResponsiveStyles(borderRadius, 'border-radius')}

    ${({ overflow }) =>
        overflow &&
        getResponsiveProp({
            type: 'overflow',
            value: overflow,
        })}

    ${({ direction }) =>
        resolveResponsiveProp({
            type: 'flex-direction',
            value: direction || 'column',
        })}


    ${({ justify }) =>
        resolveResponsiveProp({
            type: 'justify-content',
            value: justify || 'flex-start',
        })}

    ${({ align }) =>
        resolveResponsiveProp({
            type: 'align-items',
            value: align || 'flex-start',
        })}
    ${({ alignSelf }) =>
        alignSelf &&
        resolveResponsiveProp({
            type: 'align-self',
            value: alignSelf || 'flex-start',
        })}

    ${({ wrap }) =>
        resolveResponsiveProp({
            type: 'flex-wrap',
            value: wrap || 'nowrap',
        })}

    ${({ borderColor }) =>
        borderColor &&
        css`
            border-color: ${({ theme }) => theme.colors[borderColor]};
        `}

    ${({ borderWidth }) =>
        borderWidth &&
        css`
            border-width: ${borderWidth}px;
            border-style: solid;
        `}

    ${({ borderRadius }) =>
        borderRadius &&
        css`
            border-radius: ${borderRadius}px;
        `}

    ${({ gap, direction }) => {
        if (!gap) return css``
        const isDirectionArray = Array.isArray(direction)
        const { isTablet } = useResolutions()

        const typeFromDirection = {
            column: 'margin-top',
            'column-reverse': 'margin-top',
            row: 'margin-right',
            'row-reverse': 'margin-left',
        }

        const mobileDirection = isDirectionArray
            ? direction[0]
            : direction || 'column'

        const desktopDirection = isDirectionArray
            ? direction[1]
            : direction || 'column'

        const childrenGap = css`
            ${resolveResponsiveProp({
                type: isTablet
                    ? typeFromDirection[mobileDirection]
                    : typeFromDirection[desktopDirection],
                value: gap,
                unit: 'px',
            })};
        `

        const zeroGap = css`
            &:last-child {
                ${resolveResponsiveProp({
                    type: isTablet
                        ? typeFromDirection[mobileDirection]
                        : typeFromDirection[desktopDirection],
                    value: 0,
                    unit: 'px',
                })};
            }
        `

        return (isTablet && mobileDirection === 'column') ||
            (!isTablet && desktopDirection === 'column')
            ? css`
                  & > * {
                      & + * {
                          ${childrenGap};
                      }
                  }
              `
            : css`
                  & > * {
                      ${childrenGap};
                      ${zeroGap};
                  }
              `
    }};

    ${({ theme, bgColor }) =>
        bgColor &&
        css`
            background-color: ${theme.colors[bgColor]};
        `};

    ${({ minWidth }) => {
        if (!minWidth) return css``

        return resolveResponsiveProp({
            type: 'min-width',
            value: minWidth,
            unit: 'px',
        })
    }}

    ${({ maxWidth }) => {
        if (!maxWidth) return css``

        return resolveResponsiveProp({
            type: 'max-width',
            value: maxWidth,
            unit: 'px',
        })
    }}

    ${({ minHeight }) => {
        if (!minHeight) return css``
        return resolveResponsiveProp({
            type: 'min-height',
            value: minHeight,
            unit: 'px',
        })
    }}

    ${spacingMixin}
`
export default Box
